import { Injectable } from '@angular/core';
import { Subject, BehaviorSubject, interval, of, timer, race, Observable } from 'rxjs';
import { filter, switchMap, tap, map, takeUntil, auditTime, take } from 'rxjs/operators';
import { ConfigService } from 'providers/config.service';
import { AuthService } from 'providers/auth.service';
import { CsmsEvent } from './events/events';
import { v4 as uuidv4 } from 'uuid';
import * as _ from 'lodash';

const PING_INTERVAL_MS = 5000;
const PING_TIMEOUT_MS = 4500;
const RECONNECT_TIME_MS = 5000;

export type CsmsNotification = KpisUpdatedNotification | ChargingStationUpdated | PongNotification;

export interface KpisUpdatedNotification {
    type: 'KpisUpdated';
    browserReceiveTimestamp: string;
}

export interface ChargingStationUpdated {
    type: 'ChargingStationUpdated';
    stationId: string;
    locationUid?: string;
    changeSources: string[];
    changedSchemas: RowTypes[];
    /** Timestamp at charger */
    chargerTimestamp?: string;
    /** When event was stored to database */
    cloudTimestamp: string;
    browserReceiveTimestamp: string;
    events: CsmsEvent[];
}

export type RowTypes =
    | 'alert'
    | 'alertHistory'
    | 'authorizationList'
    | 'authorizationUserCollection'
    | 'connection'
    | 'connectionHistory'
    | 'commissioning'
    | 'counter'
    | 'device'
    | 'deviceHistory'
    | 'evse'
    | 'firmwarePackage'
    | 'group'
    | 'logStream'
    | 'ocppMessage'
    | 'ocppRequest'
    | 'settings'
    | 'settingsHistory'
    | 'station'
    | 'status'
    | 'statusHistory'
    | 'systemMetrics'
    | 'systemMetricsHistory'
    | 'systemStructure'
    | 'systemStructureHistory'
    | 'ticket'
    | 'ticketComment'
    | 'tx'
    | 'txLatest'
    | 'txSample'
    | 'txUpdate'
    | 'updateProcess'
    | 'updateProcessNotification'
    | 'userCollection'
    | 'userCollectionToken'
    | 'ev'
    | 'evSchedule'
    | 'v2icp'
    | 'location'
    | 'externalNotificationSettings';

export interface PongNotification {
    type: 'pong';
    payload: string;
    browserReceiveTimestamp: string;
}

export type MessageFromClient = PingMessageFromClient;

export interface PingMessageFromClient {
    action: 'ping';
    payload: string;
}

export interface SubscribeMessageFromClient {
    action: 'subscribe';
    stationIds?: string[];
    locationIds?: string[];
}

const notification$ = new Subject<CsmsNotification>();
const connected$ = new BehaviorSubject<boolean>(false);

/**
 * Service for subscribing to and receiving notifications from the backend.
 *
 * subscribeUntil should be called by each component that needs to receive notifications from specific stations or locations
 *
 * Subscriptions made in quick succession will not result in multiple messages being sent to the backend, reducing number of messages sent to the backend and avoiding race conditions.
 * Subscribe messages will only be sent to the backend if the sets of subscribed stations or locations actually changes
 * Subscriptions made or removed for one component will not affect the flow of notifications for another. The flow of notifications for a specific station or location will only stop when all components have unsubscribed from it.
 */
@Injectable({ providedIn: 'root' })
export class NotificationsService {
    private _componentSubscriptions: {
        [key: string]: { stationIds: string[]; locationIds: string[] };
    } = {};

    // Keep track of the stationIds and locationIds that are currently subscribed to in the backend
    private _backendSubscribedStationIds = new Set<string>();
    private _backendSubscribedLocationIds = new Set<string>();
    private readonly sendSubscribeMessage$ = new Subject<{ stationIds?: string[]; locationIds?: string[] }>();

    constructor(
        private readonly NotificationsManagementService: NotificationsManagementService,
        private readonly config: ConfigService
    ) {
        this.sendSubscribeMessage$
            .pipe(auditTime(config.notificationsSendMessageAuditTimeMs)) // Wait some time to reduce number of messages to backend and prevent race conditions
            .subscribe((sub) => {
                this.NotificationsManagementService.sendSubscribe(sub.stationIds, sub.locationIds);
            });

        // Sync current state on connect, subscription state is preserved across reconnects
        connected$.pipe(filter((connected) => connected)).subscribe(() => {
            // Sync the current state to the backend
            this.syncCurrentStatetoBackend();
        });
    }

    private syncCurrentStatetoBackend() {
        this._backendSubscribedLocationIds = new Set();
        this._backendSubscribedStationIds = new Set();
        this.syncSubscribedStationIds(true);
    }

    get notification$() {
        return notification$;
    }
    get connected$() {
        return connected$;
    }

    /**
     *  Returns the set of stationIds that are currently subscribed to across all components
     */
    private get allStationIdsOfSubscriptions() {
        const subscribedStationIdsFlat = Object.keys(this._componentSubscriptions)
            .map((key) => this._componentSubscriptions[key].stationIds)
            .flat();

        // Deduplicate with Set
        return new Set(subscribedStationIdsFlat);
    }

    /**
     * Returns the set of locationIds that are currently subscribed to across all components
     */
    private get allLocationIdsOfSubscriptions() {
        const subscribedLocationIdsFlat = Object.keys(this._componentSubscriptions)
            .map((key) => this._componentSubscriptions[key].locationIds)
            .flat();

        // Deduplicate with Set
        return new Set(subscribedLocationIdsFlat);
    }

    /**
     * Subscribe to events from specific stations or locations. Should be called by each component that needs to receive notifications from specific stations or locations.
     *
     * @param sub stations and locations to subscribe to
     * @param until$ observable that will trigger the removal of the subscription
     */
    subscribeUntil(sub: { stationIds?: string[]; locationIds?: string[] }, until$: Observable<any>) {
        const key = this.createComponentSubscription(sub.stationIds ?? [], sub.locationIds ?? []);

        until$.pipe(take(1)).subscribe(() => {
            this.removeComponentSubscription(key);
        });
    }

    /**
     * Subscription will never be removed...
     * Implementing this so we can move forward with stations.service, hopefully this is a temporary solution
     * station.service doesn't have any mechanism of unsubscribing
     *
     * @param sub stations and locations to subscribe to
     */
    subscribeForever(sub: { stationIds?: string[]; locationIds?: string[] }) {
        this.createComponentSubscription(sub.stationIds ?? [], sub.locationIds ?? []);
    }

    /**
     * @returns subscription key that can be used to remove the subscription
     */
    private createComponentSubscription(stationIds: string[] = [], locationIds: string[] = []) {
        const subscriptionKey = uuidv4();
        this._componentSubscriptions[subscriptionKey] = { stationIds, locationIds };

        this.syncSubscribedStationIds();

        return subscriptionKey;
    }

    private removeComponentSubscription(subscriptionKey: string) {
        delete this._componentSubscriptions[subscriptionKey];

        this.syncSubscribedStationIds();
    }

    /**
     * Syncs the subscribed station ids with the backend, if necessary
     *
     * Compares the flat lists of stations & locations from all subscriptions with the stations and locations that are currently subscribed to in the backend.
     * If they are different, sends a new subscription message to the backend.
     */
    private syncSubscribedStationIds(forceSync: boolean = false) {
        const allStationIdsFromSubscriptions = this.allStationIdsOfSubscriptions;
        const allLocationIdsOfSubscriptions = this.allLocationIdsOfSubscriptions;

        if (
            forceSync ||
            !_.isEqual(this._backendSubscribedStationIds, allStationIdsFromSubscriptions) ||
            !_.isEqual(this._backendSubscribedLocationIds, allLocationIdsOfSubscriptions)
        ) {
            this._backendSubscribedStationIds = new Set(allStationIdsFromSubscriptions);
            this._backendSubscribedLocationIds = new Set(allLocationIdsOfSubscriptions);

            this.sendSubscribeMessage$.next({
                stationIds: Array.from(this.allStationIdsOfSubscriptions),
                locationIds: Array.from(this.allLocationIdsOfSubscriptions),
            });
        }
    }
}

@Injectable({ providedIn: 'root' })
export class NotificationsManagementService {
    private ws: WebSocket;

    private disconnectRequested$ = new Subject<void>();
    public connectedTenantId: string;

    constructor(
        private authService: AuthService,
        private config: ConfigService
    ) {}

    connect() {
        // connect, and if we're not connected, attempt connection again in 5 seconds
        let first = true;
        connected$
            .pipe(
                filter((c) => c === false),
                switchMap(() => (first ? of(0) : timer(RECONNECT_TIME_MS))),
                tap(() => {
                    first = false;
                }),
                takeUntil(this.disconnectRequested$)
            )
            .subscribe(() => {
                this.ws = this._connect();
            });

        // start pinging every 5 seconds after connection ready
        connected$
            .pipe(
                filter((c) => c === true),
                switchMap(() =>
                    interval(PING_INTERVAL_MS).pipe(
                        takeUntil(connected$.pipe(filter((c) => c === false))) // ping until we lose connection
                    )
                ),
                map(() => {
                    if (this.ws.readyState === WebSocket.CLOSED || this.ws.readyState === WebSocket.CLOSING) {
                        console.info('[Notifications WebSocket] Socket in CLOSED or CLOSING state');
                        this._disconnect();
                        return undefined;
                    }

                    return this.sendPing();
                }),
                // wait for pong response...
                switchMap((payload) =>
                    race(
                        notification$.pipe(filter((msg) => msg.type === 'pong' && msg.payload === payload)),
                        timer(PING_TIMEOUT_MS).pipe(map(() => undefined))
                    )
                ),
                takeUntil(this.disconnectRequested$)
            )
            .subscribe((p) => {
                // ping received, all good
                if (!p) {
                    // ping not received, timed out
                    console.error('[Notifications WebSocket] Pong not received');
                    this._disconnect();
                }
            });
    }

    close() {
        this.ws.close();
    }

    disconnect() {
        this.disconnectRequested$.next();
        this._disconnect();
    }

    sendSubscribe(stationIds?: string[], locationIds?: string[]) {
        if (this.ws?.readyState !== WebSocket.OPEN) {
            throw new Error('WebSocket not connected');
        }

        const message: SubscribeMessageFromClient = { action: 'subscribe', stationIds, locationIds };
        try {
            this.ws.send(JSON.stringify(message));
        } catch (e) {
            console.error('[Notifications WebSocket] Received setStationsSubscription send error', e);
        }
    }

    private sendPing() {
        const payload = '' + Date.now();
        const pingMsg: PingMessageFromClient = { action: 'ping', payload };
        if (this.ws) {
            try {
                this.ws.send(JSON.stringify(pingMsg));
            } catch (e) {
                // do not let sending to a disconnected socket crash our observable
                console.warn('[Notifications WebSocket] Received ping send error', e);
            }
        }

        return payload;
    }

    private _disconnect() {
        if (this.ws) {
            console.info('[Notifications WebSocket] Disconnecting');
            connected$.next(false);
            this.ws.close();
        }
        this.ws = null;
    }

    private _connect() {
        console.info('[Notifications WebSocket] Attempting connection', this.config.notificationsWebSocketUrl);

        const ws = new WebSocket(
            `${this.config.notificationsWebSocketUrl}?token=${encodeURIComponent(this.authService.jwt)}`
        );
        ws.onopen = () => {
            this.connectedTenantId = this.authService.profile.tenantId;
            console.info('[Notifications WebSocket] Connected');
            connected$.next(true);
        };
        ws.onerror = (e) => {
            console.error('[Notifications WebSocket] Connection error', e);
            this._disconnect();
        };
        ws.onclose = (ev) => {
            console.info('[Notifications WebSocket] Connection closed');
        };
        ws.onmessage = (msg) => {
            const data = JSON.parse(msg.data) as CsmsNotification;
            notification$.next({
                ...data,
                browserReceiveTimestamp: new Date().toISOString(),
            });
        };

        return ws;
    }
}
